import React, { useEffect, useState } from "react";
import { BaseTable } from "@app/components/common/BaseTable/BaseTable";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseForm } from "@app/components/common/forms/BaseForm/BaseForm";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import {
  message,
  Modal,
  Input,
  Select,
  Form,
  Row,
  Col,
  InputNumber,
  Card,
  Typography,
  Divider,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  adminApi,
  HolidayBalance,
  HolidayBalanceUpdateData,
  LeaveType,
} from "@app/api/admin";
import dayjs from "dayjs";
import { httpClient } from "@app/api/base";

const { Title, Text } = Typography;

interface HolidayBalanceManagementProps {
  employeeId?: number;
  employeeName?: string;
}

const HolidayBalanceManagement: React.FC<HolidayBalanceManagementProps> = ({
  employeeId,
  employeeName,
}) => {
  const [form] = BaseForm.useForm();
  const [holidayBalances, setHolidayBalances] = useState<HolidayBalance[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(false);
  const [leaveTypesLoading, setLeaveTypesLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState<HolidayBalance | null>(
    null
  );

  // 获取假期类型数据
  const fetchLeaveTypes = async () => {
    try {
      setLeaveTypesLoading(true);
      const { data } = await adminApi.leaveTypes.list({ is_active: true });
      setLeaveTypes(data.data || []);
    } catch (error: any) {
      setLeaveTypes([]);
    } finally {
      setLeaveTypesLoading(false);
    }
  };

  // 获取假期余额数据
  const fetchHolidayBalances = async () => {
    if (!employeeId) return;

    try {
      setLoading(true);
      const { data } = await adminApi.employees.getHolidayBalance(employeeId);
      console.log(data);
      setHolidayBalances(
        data?.data?.filter((item) =>  item.total_days !== 0 && item.remaining_days >= 0) || []
      );
    } catch (error: any) {
      message.error(error.message || "获取假期余额失败");
      setHolidayBalances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record: HolidayBalance) => {
    Modal.confirm({
      title: "确认删除吗？",
      okText: "确认",
      okType: "danger",
      onOk: async () => {
        try {
          await adminApi.employees.deleteHolidayBalance(record.id);
          message.success("删除成功");
          fetchHolidayBalances();
        } catch (error: any) {
          message.error(error.message || "删除失败");
        }
      },
    });
  };

  useEffect(() => {
    fetchLeaveTypes();
    fetchHolidayBalances();
  }, [employeeId]);

  const getLeaveTypeName = (leaveType: string) => {
    return leaveTypes.find((item) => item.code === leaveType)?.name || "-";
  };

  // 表格列定义
  const columns = [
    {
      title: "假期类型",
      dataIndex: "leave_type",
      width: 100,
      render: (leaveType: string) => getLeaveTypeName(leaveType),
    },
    {
      title: "年份",
      dataIndex: "year",
      width: 100,
    },
    {
      title: "总天数",
      dataIndex: "total_days",
      width: 100,
      render: (days: number) => `${days}天`,
    },
    {
      title: "已使用",
      dataIndex: "used_days",
      width: 100,
      render: (days: number) => `${days}天`,
    },
    {
      title: "剩余天数",
      dataIndex: "remaining_days",
      width: 120,
      render: (days: number) => (
        <Text type={days <= 0 ? "danger" : "success"}>{days}天</Text>
      ),
    },
    {
      title: "结转天数",
      dataIndex: "transferred_days",
      width: 120,
      render: (days: number) => `${days}天`,
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      width: 180,
      render: (date: Date) => dayjs(date).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "更新时间",
      dataIndex: "last_updated_at",
      width: 180,
      render: (date: Date) => dayjs(date).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "操作",
      dataIndex: "actions",
      width: 180,
      fixed: "right" as const,
      render: (_: any, record: HolidayBalance) => (
        <BaseSpace>
          <BaseButton
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </BaseButton>
          <BaseButton
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(record)}
          >
            删除
          </BaseButton>
        </BaseSpace>
      ),
    },
  ];

  // 处理编辑
  const handleEdit = (record: HolidayBalance) => {
    setEditingRecord(record);
    form.setFieldsValue({
      leave_type: record.leave_type,
      year: record.year,
      total_days: record.total_days,
      transferred_days: record.transferred_days,
    });
    setIsModalVisible(true);
  };

  // 处理新增
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      year: new Date().getFullYear(),
      total_days: 0,
      transferred_days: 0,
    });
    setIsModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async () => {
    if (!employeeId) return;

    try {
      const values = await form.validateFields();
      setModalLoading(true);

      const data: HolidayBalanceUpdateData = {
        leave_type: values.leave_type,
        year: values.year,
        total_days: values.total_days,
        transferred_days: values.transferred_days || 0,
      };

      await adminApi.employees.setHolidayBalance(employeeId, data);
      message.success(editingRecord ? "更新成功" : "添加成功");
      setIsModalVisible(false);
      fetchHolidayBalances();
    } catch (error: any) {
      console.error(error);
      //   message.error(error.message || "操作失败");
    } finally {
      setModalLoading(false);
    }
  };

  // 取消操作
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  if (!employeeId) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Text type="secondary">请先选择员工</Text>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                {employeeName} - 假期余额管理
              </Title>
            </Col>
            <Col>
              <BaseButton
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                添加假期余额
              </BaseButton>
            </Col>
          </Row>
        </div>

        <BaseTable
          bordered
          columns={columns}
          dataSource={holidayBalances}
          loading={loading}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 编辑/新增模态框 */}
      <Modal
        title={editingRecord ? "编辑假期余额" : "添加假期余额"}
        visible={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={modalLoading}
        width={500}
        destroyOnClose
      >
        <BaseForm
          form={form}
          layout="vertical"
          initialValues={{ total_days: 0, transferred_days: 0 }}
        >
          <BaseForm.Item
            label="假期类型"
            name="leave_type"
            rules={[{ required: true, message: "请选择假期类型" }]}
          >
            <Select
              placeholder="请选择假期类型"
              loading={leaveTypesLoading}
              showSearch
              filterOption={(input, option) =>
                option?.children
                  ?.toString()
                  .toLowerCase()
                  .includes(input.toLowerCase()) || false
              }
            >
              {leaveTypes.map((leaveType) => (
                <Select.Option key={leaveType.id} value={leaveType.code}>
                  {leaveType.name}
                </Select.Option>
              ))}
            </Select>
          </BaseForm.Item>

          <BaseForm.Item
            label="年份"
            name="year"
            rules={[{ required: true, message: "请选择年份" }]}
          >
            <InputNumber
              placeholder="请输入年份"
              min={2020}
              max={2090}
              style={{ width: "100%" }}
            />
          </BaseForm.Item>

          <BaseForm.Item
            label="总天数"
            name="total_days"
            rules={[{ required: true, message: "请输入总天数" }]}
          >
            <InputNumber
              placeholder="请输入总天数"
              min={0}
              max={365}
              style={{ width: "100%" }}
              addonAfter="天"
            />
          </BaseForm.Item>

          <BaseForm.Item
            label="结转天数"
            name="transferred_days"
            tooltip="从上一年结转过来的天数"
          >
            <InputNumber
              placeholder="请输入结转天数"
              min={0}
              max={365}
              style={{ width: "100%" }}
              addonAfter="天"
            />
          </BaseForm.Item>
        </BaseForm>
      </Modal>
    </div>
  );
};

export default HolidayBalanceManagement;
